<script setup lang="ts">
import { computed, ref } from 'vue'
import { formatting, useLocalization } from '@hantera/portal-app'
import { DateOnly } from '@hantera/design-system'
import { read, utils } from 'xlsx'
import useBackend from '@/composition/useBackend'
import type { BatchScheduleResult, JobDefinition, JobDefinitionParameter } from '@/backend/jobs.types'
import ParamInput from './ParamInput.vue'
import {
  defaultForKind,
  isEmptyValue,
  parseParamType,
  serializeValue,
  type ParamKind,
} from './paramTypes'

const props = defineProps<{
  jobDefinition: JobDefinition
  onClose: () => void
}>()

const t = useLocalization('jobs')
const { jobs: jobsService } = useBackend()

type Step = 'upload' | 'mapping' | 'progress'

const STATIC_SOURCE = '__static__'

const step = ref<Step>('upload')
const fileError = ref<string | null>(null)

const isDragging = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

const sheetColumns = ref<string[]>([])
const sheetRows = ref<Record<string, unknown>[]>([])
const columnMapping = ref<Record<string, string>>({})
const staticValues = ref<Record<string, unknown>>({})

const scheduledCount = ref(0)
const errors = ref<{ row: number; error: string }[]>([])
const isDone = ref(false)
const wasStopped = ref(false)
const totalRows = ref(0)

let abortController: AbortController | null = null

const parameterNames = computed(() => {
  if (!props.jobDefinition.parameters) return []
  return Object.keys(props.jobDefinition.parameters)
})

const parameters = computed(() => props.jobDefinition.parameters ?? {})

const hasParameters = computed(() => parameterNames.value.length > 0)

const kinds = computed<Record<string, ParamKind>>(() => {
  const result: Record<string, ParamKind> = {}
  for (const name of parameterNames.value) {
    result[name] = parseParamType(parameters.value[name]?.type)
  }
  return result
})

const requiredParams = computed(() =>
  parameterNames.value.filter((p) => parameters.value[p]?.isRequired),
)

function isParamSatisfied(param: string): boolean {
  const source = columnMapping.value[param]
  if (source === STATIC_SOURCE) {
    return !isEmptyValue(kinds.value[param], staticValues.value[param])
  }
  return Boolean(source)
}

const canSchedule = computed(() =>
  !hasParameters.value || requiredParams.value.every((p) => isParamSatisfied(p)),
)

function paramType(param: string): string {
  return parameters.value[param]?.type ?? 'text'
}

function paramInfo(param: string): JobDefinitionParameter | undefined {
  return parameters.value[param]
}

function handleDragOver(event: DragEvent) {
  event.preventDefault()
  isDragging.value = true
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault()
  isDragging.value = false
}

function handleDrop(event: DragEvent) {
  event.preventDefault()
  isDragging.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    processFile(files[0])
  }
}

function handleFileInput(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0) {
    processFile(files[0])
  }
}

function processFile(file: File) {
  fileError.value = null

  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target!.result as ArrayBuffer)
      const workbook = read(data, { type: 'array', cellDates: true })
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
      const json = utils.sheet_to_json<Record<string, unknown>>(firstSheet)

      if (json.length === 0) {
        fileError.value = t('detail.batchScheduleDialog.emptyFile')
        return
      }

      sheetColumns.value = Object.keys(json[0])
      sheetRows.value = json

      autoMatchColumns()
      step.value = 'mapping'
    } catch {
      fileError.value = t('detail.batchScheduleDialog.parseError')
    }
  }
  reader.readAsArrayBuffer(file)
}

function autoMatchColumns() {
  const mapping: Record<string, string> = {}
  const statics: Record<string, unknown> = {}
  for (const param of parameterNames.value) {
    mapping[param] = ''
    statics[param] = defaultForKind(kinds.value[param])
    const lowerParam = param.toLowerCase()
    const match = sheetColumns.value.find(
      (col) => col.toLowerCase() === lowerParam,
    )
    if (match) {
      mapping[param] = match
    }
  }
  columnMapping.value = mapping
  staticValues.value = statics
}

function resolveStaticValue(param: string): unknown {
  const kind = kinds.value[param]
  const value = staticValues.value[param]
  if (isEmptyValue(kind, value)) return undefined
  try {
    return serializeValue(kind, value)
  } catch {
    return undefined
  }
}

function excelSerialToDate(serial: number): Date {
  // Excel epoch: 1899-12-30, with the Lotus 1-2-3 leap year bug
  const epoch = new Date(Date.UTC(1899, 11, 30))
  const ms = serial * 86400000
  return new Date(epoch.getTime() + ms)
}

function coerceValue(value: unknown, type: string): unknown {
  if (value === null || value === undefined || value === '') return null

  switch (type) {
    case 'number': {
      const num = Number(String(value))
      return isNaN(num) ? String(value) : num
    }

    case 'boolean':
    case 'bool': {
      const lower = String(value).toLowerCase()
      if (lower === 'true' || lower === '1' || lower === 'yes') return true
      if (lower === 'false' || lower === '0' || lower === 'no') return false
      return String(value)
    }

    case 'instant': {
      if (value instanceof Date) {
        return value.toISOString()
      }
      if (typeof value === 'number') {
        return excelSerialToDate(value).toISOString()
      }
      const parsed = new Date(String(value))
      return isNaN(parsed.getTime()) ? String(value) : parsed.toISOString()
    }

    case 'date': {
      if (value instanceof Date) {
        return DateOnly.fromDate(value).toString()
      }
      if (typeof value === 'number') {
        return DateOnly.fromDate(excelSerialToDate(value)).toString()
      }
      const raw = String(value).trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        try {
          return DateOnly.parse(raw).toString()
        } catch {
          return raw
        }
      }
      const parsed = new Date(raw)
      return isNaN(parsed.getTime()) ? raw : DateOnly.fromDate(parsed).toString()
    }

    default:
      return String(value)
  }
}

const mappedPreviewRows = computed(() => {
  return sheetRows.value.slice(0, 5).map((row) => {
    const mapped: Record<string, unknown> = {}
    for (const param of parameterNames.value) {
      const sourceCol = columnMapping.value[param]
      if (sourceCol === STATIC_SOURCE) {
        mapped[param] = resolveStaticValue(param)
      } else if (sourceCol) {
        mapped[param] = coerceValue(row[sourceCol], paramType(param))
      } else {
        mapped[param] = undefined
      }
    }
    return mapped
  })
})

function buildMappedRows(): Record<string, unknown>[] {
  const staticResolved: Record<string, unknown> = {}
  for (const param of parameterNames.value) {
    if (columnMapping.value[param] === STATIC_SOURCE) {
      staticResolved[param] = resolveStaticValue(param)
    }
  }

  return sheetRows.value.map((row) => {
    const mapped: Record<string, unknown> = {}
    for (const param of parameterNames.value) {
      const sourceCol = columnMapping.value[param]
      if (sourceCol === STATIC_SOURCE) {
        if (staticResolved[param] !== undefined) {
          mapped[param] = staticResolved[param]
        }
      } else if (sourceCol) {
        mapped[param] = coerceValue(row[sourceCol], paramType(param))
      }
    }
    return mapped
  })
}

function formatPreviewValue(value: unknown, type: string): string {
  if (value === undefined) return '—'
  if (value === null) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (type === 'instant' && typeof value === 'string') {
    return formatting.dateTime(value)
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

async function startScheduling() {
  step.value = 'progress'
  scheduledCount.value = 0
  errors.value = []
  isDone.value = false
  wasStopped.value = false

  const mappedRows = hasParameters.value ? buildMappedRows() : sheetRows.value.map(() => ({}))
  totalRows.value = mappedRows.length

  abortController = new AbortController()

  let rowIndex = 0
  await jobsService.scheduleBatch(
    props.jobDefinition.jobDefinitionId,
    mappedRows,
    (result: BatchScheduleResult) => {
      rowIndex++
      if (result.jobId) {
        scheduledCount.value++
      } else {
        errors.value.push({ row: rowIndex, error: result.error ?? t('detail.batchScheduleDialog.unknownError') })
      }
    },
    abortController.signal,
  )

  abortController = null
  isDone.value = true
}

function stopScheduling() {
  abortController?.abort()
  abortController = null
  wasStopped.value = true
  isDone.value = true
}
</script>

<template>
  <div class="batch-schedule-dialog p2">
    <!-- Step 1: Upload -->
    <template v-if="step === 'upload'">
      <div class="box-content p">
          <div
            class="dropzone"
            :class="{ 'dropzone--dragging': isDragging }"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
          >
            <input
              ref="fileInputRef"
              type="file"
              accept=".csv,.xlsx,.xls"
              class="dropzone-input"
              @change="handleFileInput"
            />

            <div class="dropzone-content">
              <div class="dropzone-icon">📄</div>
              <div class="dropzone-text">
                <span class="dropzone-primary">
                  {{ t('detail.batchScheduleDialog.dropzoneText') }}
                </span>
                <span class="dropzone-secondary">
                  {{ t('detail.batchScheduleDialog.dropzoneHint') }}
                </span>
              </div>
            </div>
          </div>

        <div v-if="fileError" class="error">{{ fileError }}</div>
      </div>
      <template v-if="parameterNames.length > 0">
          <div class="box-content-label">
            {{ t('detail.batchScheduleDialog.expectedColumns') }}
          </div>

          <div class="box-content p">
            <table>
              <thead>
                <tr>
                  <th>{{ t('detail.batchScheduleDialog.column') }}</th>
                  <th>{{ t('detail.batchScheduleDialog.type') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in parameterNames" :key="p">
                  <td>{{ p }}<span v-if="!paramInfo(p)?.isRequired" class="optional-badge"> optional</span></td>
                  <td>{{ paramType(p) }}</td>
                </tr>
              </tbody>
            </table>
            
          </div>
        <div class="field-help">
          {{ t('detail.batchScheduleDialog.columnHint') }}
        </div>
      </template>

      <div class="dialog-actions">
        <button class="btn-secondary" @click="onClose">{{ t('detail.batchScheduleDialog.cancel') }}</button>
      </div>
    </template>

    <!-- Step 2: Column Mapping -->
    <template v-if="step === 'mapping'">
      <template v-if="hasParameters">
        <div class="box-content-label">
          {{ t('detail.batchScheduleDialog.mapColumns') }}
        </div>
        <p class="box-content-help">
          {{ t('detail.batchScheduleDialog.mapHelp', { count: sheetRows.length }) }}
        </p>
        <div class="box-content p">
          
          <table class="mapping-table">
            <thead>
              <tr
                class="mapping-row"
              >
                <th>{{ t('detail.batchScheduleDialog.parameter') }}</th>
                <th>{{ t('detail.batchScheduleDialog.type') }}</th>
                <th>{{ t('detail.batchScheduleDialog.sourceColumn') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="mapping-row"
                v-for="param in parameterNames"
                :key="param"
              >
                <td class="monospace">{{ param }}<span v-if="!paramInfo(param)?.isRequired" class="optional-badge"> optional</span></td>
                <td class="param-type-cell">{{ paramType(param) }}</td>
                <td class="source-cell">
                  <select v-model="columnMapping[param]">
                    <option value="">{{ t('detail.batchScheduleDialog.selectColumn') }}</option>
                    <option v-for="col in sheetColumns" :key="col" :value="col">
                      {{ col }}
                    </option>
                    <option :value="STATIC_SOURCE">{{ t('detail.batchScheduleDialog.staticValue') }}</option>
                  </select>
                  <ParamInput
                    v-if="columnMapping[param] === STATIC_SOURCE"
                    class="static-input"
                    :kind="kinds[param]"
                    :model-value="staticValues[param]"
                    @update:model-value="(v) => (staticValues[param] = v)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>        

        <div v-if="mappedPreviewRows.length > 0" class="preview-section">
          <div class="box-content-label">{{ t('detail.batchScheduleDialog.previewLabel', { count: mappedPreviewRows.length }) }}</div>
          <div class="box-content preview-table-wrapper">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="param in parameterNames" :key="param">{{ param }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in mappedPreviewRows" :key="i">
                  <td
                    v-for="param in parameterNames"
                    :key="param"
                    :class="{ 'unmapped': row[param] === undefined }"
                  >{{ formatPreviewValue(row[param], paramType(param)) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="box-content p">
          <p>
            {{ t('detail.batchScheduleDialog.fileReady') }}
          </p>
        </div>
      </template>

      <div class="dialog-actions">
        <button class="btn-secondary" @click="step = 'upload'">{{ t('detail.batchScheduleDialog.back') }}</button>
        <button
          class="btn-primary"
          :disabled="!canSchedule"
          @click="startScheduling"
        >
          {{ t('detail.batchScheduleDialog.scheduleCount', { count: sheetRows.length }) }}
        </button>
      </div>
    </template>

    <!-- Step 3: Progress -->
    <template v-if="step === 'progress'">
      <div class="step-header">
        <h3>
          {{ wasStopped
            ? t('detail.batchScheduleDialog.stopped')
            : isDone
              ? t('detail.batchScheduleDialog.complete')
              : t('detail.batchScheduleDialog.scheduling') }}
        </h3>
      </div>

      <div class="progress-stats">
        <div class="stat">
          <span class="stat-value">{{ scheduledCount }}</span>
          <span class="stat-label">{{ t('detail.batchScheduleDialog.scheduled') }}</span>
        </div>
        <div class="stat stat-error" v-if="errors.length > 0">
          <span class="stat-value">{{ errors.length }}</span>
          <span class="stat-label">{{ t('detail.batchScheduleDialog.errors') }}</span>
        </div>
        <div class="stat stat-total">
          <span class="stat-value">{{ totalRows }}</span>
          <span class="stat-label">{{ t('detail.batchScheduleDialog.total') }}</span>
        </div>
      </div>

      <div class="progress-bar-wrapper">
        <div
          class="progress-bar"
          :style="{ width: `${totalRows > 0 ? ((scheduledCount + errors.length) / totalRows) * 100 : 0}%` }"
        />
      </div>

      <div v-if="errors.length > 0" class="error-list">
        <span class="label">{{ t('detail.batchScheduleDialog.errors') }}</span>
        <div class="error-list-items">
          <div v-for="err in errors" :key="err.row" class="error-item">
            <span class="error-row">{{ t('detail.batchScheduleDialog.row', { row: err.row }) }}</span>
            <span class="error-message">{{ err.error }}</span>
          </div>
        </div>
      </div>

      <div class="dialog-actions">
        <button
          v-if="!isDone"
          class="btn-danger"
          @click="stopScheduling"
        >
          {{ t('detail.batchScheduleDialog.stop') }}
        </button>
        <button
          v-else
          class="btn-primary"
          @click="onClose"
        >
          {{ t('detail.batchScheduleDialog.close') }}
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.batch-schedule-dialog {
  display: flex;
  flex-direction: column;
  min-width: 320px;
  max-width: 700px;
}

.step-header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.step-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: hsl(var(--color-20));
}

.help-text {
  margin: 0;
  font-size: 13px;
  color: hsl(var(--color-40));
  line-height: 1.5;
}

/* Dropzone */

.dropzone {
  position: relative;
  border: 2px dashed hsl(var(--color-70));
  border-radius: 0.5rem;
  padding: 2rem;
  cursor: pointer;
  transition: border-color 150ms, background-color 150ms;
}

.dropzone:hover {
  border-color: hsl(var(--color-50));
  background-color: hsl(var(--color-95));
}

.dropzone--dragging {
  border-color: hsl(var(--color-primary));
  background-color: hsl(var(--color-primary-95));
}

.dropzone-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
}

.dropzone-icon {
  font-size: 2rem;
}

.dropzone-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dropzone-primary {
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(var(--color-20));
}

.dropzone-secondary {
  font-size: 0.75rem;
  color: hsl(var(--color-50));
}

.dropzone-params {
  font-size: 0.75rem;
  color: hsl(var(--color-50));
  margin-top: 0.25rem;
}

.dropzone-params code {
  font-size: 0.75rem;
  font-family: monospace;
  color: hsl(var(--color-30));
}

.dropzone-param-type {
  color: hsl(var(--color-50));
}

/* Mapping table */

.mapping-table {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mapping-row {
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  gap: 0.75rem;
  padding: 2.5px 0;
  align-items: center;
  border-bottom: 1px solid hsl(var(--color-92));
  font-size: 13px;
}

.mapping-row:last-child {
  border-bottom: none;
}

.mapping-header {
  background: hsl(var(--color-95));
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: hsl(var(--color-40));
}

.param-type-cell {
  font-size: 12px;
}

.optional-badge {
  font-size: 10px;
  font-weight: 500;
  color: hsl(var(--color-50));
  font-style: italic;
  font-family: sans-serif;
}

.monospace {
  font-family: monospace;
}

.mapping-row select {
  width: 100%;
  padding: 0.35rem 0.5rem;
  font-size: 13px;
  border: 1px solid hsl(var(--color-80));
  border-radius: 3px;
  background: white;
}

.source-cell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.static-input {
  width: 100%;
}

/* Preview table */

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.preview-section .label {
  font-size: 12px;
  color: hsl(var(--color-50));
}

.preview-table-wrapper {
  overflow-x: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  white-space: nowrap;
}

.preview-table th {
  background: hsl(var(--color-95));
  padding: 0.4rem 0.6rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid hsl(var(--color-85));
  font-family: monospace;
}

.preview-table td {
  padding: 0.35rem 0.6rem;
  border-bottom: 1px solid hsl(var(--color-92));
}

.preview-table td.unmapped {
  color: hsl(var(--color-70));
  font-style: italic;
}

.preview-table tbody tr:last-child td {
  border-bottom: none;
}

/* Progress */

.progress-stats {
  display: flex;
  gap: 2rem;
  padding: 1rem 0;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: hsl(142, 60%, 40%);
}

.stat-error .stat-value {
  color: hsl(0, 84%, 50%);
}

.stat-total .stat-value {
  color: hsl(var(--color-40));
  font-weight: 400;
}

.stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: hsl(var(--color-50));
}

.progress-bar-wrapper {
  height: 6px;
  background: hsl(var(--color-90));
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: hsl(142, 60%, 45%);
  border-radius: 3px;
  transition: width 0.15s ease;
}

/* Errors */

.error-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.error-list .label {
  font-size: 12px;
  color: hsl(var(--color-50));
}

.error-list-items {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid hsl(0, 70%, 85%);
  border-radius: 4px;
  background: hsl(0, 84%, 97%);
}

.error-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.4rem 0.75rem;
  font-size: 12px;
  border-bottom: 1px solid hsl(0, 70%, 90%);
}

.error-item:last-child {
  border-bottom: none;
}

.error-row {
  font-weight: 600;
  color: hsl(0, 84%, 40%);
  white-space: nowrap;
}

.error-message {
  color: hsl(0, 50%, 35%);
}

.error {
  background: hsl(0, 84%, 95%);
  color: hsl(0, 84%, 40%);
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 13px;
}

/* Actions */

.dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid hsl(var(--color-90));
}
</style>
